/*
 * Pet Plus - collection sidebar filter enhancements.
 *
 * Progressive enhancement over the markup that snippets/collection-filter.liquid
 * already emits:
 *
 *   <div class="grid-uniform">
 *     <div class="grid-item widget-wrapper">
 *       <div class="title">Refine by BRAND</div>
 *       <ul class="advanced-filters">
 *         <li class="advanced-filter" data-group="BRAND"><a href="...">Acana</a></li>
 *       </ul>
 *     </div>
 *   </div>
 *
 * That snippet is also included by search.liquid, list-collections.liquid and
 * page.collection.liquid, so it is deliberately left alone - everything here
 * runs against the rendered DOM and only inside .template-collection. With
 * JavaScript off the sidebar keeps working exactly as it does today.
 *
 * The snippet emits one group per tag prefix, so a catalogue tagged
 * Brand_/Lifestage_/Form_/Target_/Administration_/... produces roughly twenty
 * stacked panels. Nothing is removed here - every group stays reachable - but
 * they are ranked, only the most useful few are shown up front, and the rest
 * fold away behind a single "More filters" disclosure.
 *
 * Per group it also adds a collapsible header with a count of what is active,
 * a live search once a group gets long, and an 8-item cap so a list of fifty
 * brands no longer runs to the bottom of the page. Above the groups it builds
 * a summary of what is currently applied, with individual removal and a
 * clear-all.
 *
 * The theme's own handler (the inline script in collection-filter.liquid)
 * binds clicks on .advanced-filter to keep each group single-select. Nothing
 * here interferes with it: the summary chips are built as new elements that
 * deliberately do not carry the .advanced-filter / .active-filter classes.
 */
(function () {
  'use strict';

  /* --- tuning ------------------------------------------------------ *
   * PRIORITY decides which groups earn a place above the fold. Edit the
   * order here to re-rank them; anything not listed still appears, sorted
   * by how many options it offers. Names are matched against the group
   * heading with a leading "refine by" stripped, lowercased.            */
  var PRIORITY = [
    'brand',
    'lifestage',
    'life stage',
    'age',
    'form',
    'food type',
    'type',
    'administration',
    'target',
    'size',
    'flavour',
    'flavor',
    'breed',
    'health',
    'range'
  ];

  var PRIMARY_GROUPS = 5; // groups shown before the "More filters" fold
  var OPEN_BY_DEFAULT = 2; // of those, how many start expanded
  var VISIBLE_LIMIT = 8; // items shown before a list is capped
  var SEARCH_MIN = 10; // a group longer than this gets a search box
  var STORE_KEY = 'pp:collection-filters:collapsed';
  var STORE_KEY_MORE = 'pp:collection-filters:more';

  /* ---------------------------------------------------------------- *
   *  Small helpers
   * ---------------------------------------------------------------- */

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  // localStorage throws in private mode / when site data is blocked, and the
  // filters must still work when it does.
  function readStore(key, fallback) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw == null ? fallback : JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* not fatal - the panel simply will not be remembered */
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function normalise(value) {
    return (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /* "Refine by BRAND" -> "brand". Used for ranking and for the search
     placeholder, so a miss degrades to an unranked group, never an error. */
  function groupName(titleText) {
    return normalise(titleText).replace(/^refine\s+by\s*/, '');
  }

  /* Collection URL with every tag stripped, preserving ?view= so clearing
     filters does not also throw the shopper back to the default page size.
     /collections/cats/administration_oral -> /collections/cats */
  function baseCollectionUrl() {
    var segments = window.location.pathname.split('/').filter(Boolean);
    var index = segments.indexOf('collections');
    var path =
      index === -1
        ? window.location.pathname
        : '/' + segments.slice(index, index + 2).join('/');
    return path + window.location.search;
  }

  /* ---------------------------------------------------------------- *
   *  Applied-filter summary
   * ---------------------------------------------------------------- */

  function buildSummary(sidebar, groups) {
    var active = [];

    groups.forEach(function (group) {
      group.items.forEach(function (item) {
        if (item.isActive) active.push(item);
      });
    });

    if (!active.length) return;

    var panel = el('div', 'widget-wrapper pp-filter-summary');
    var head = el('div', 'pp-filter-summary-head');
    head.appendChild(el('span', 'pp-filter-summary-title', 'Filtering by'));

    var clear = el('a', 'pp-filter-clear-all', 'Clear all');
    clear.href = baseCollectionUrl();
    head.appendChild(clear);
    panel.appendChild(head);

    var list = el('div', 'pp-filter-chips');

    active.forEach(function (item) {
      // The theme renders active filters through link_to_remove_tag, so the
      // anchor's own href already removes just this tag.
      var chip = el('a', 'pp-filter-chip');
      chip.href = item.link ? item.link.getAttribute('href') : baseCollectionUrl();
      chip.title = 'Remove filter: ' + item.label;
      chip.appendChild(el('span', 'pp-filter-chip-label', item.label));
      chip.appendChild(el('span', 'pp-filter-chip-x', 'Ã—'));
      list.appendChild(chip);
    });

    panel.appendChild(list);
    sidebar.insertBefore(panel, sidebar.firstChild);
  }

  /* ---------------------------------------------------------------- *
   *  One filter group
   * ---------------------------------------------------------------- */

  function enhanceGroup(wrapper, index) {
    var title = wrapper.querySelector('.title');
    var list = wrapper.querySelector('.advanced-filters');
    if (!title || !list) return null;

    var items = [].slice.call(list.querySelectorAll('.advanced-filter')).map(function (li) {
      var link = li.querySelector('a');
      var text = (link ? link.textContent : li.textContent) || '';
      return {
        node: li,
        link: link,
        label: text.trim(),
        search: normalise(text),
        isActive: li.classList.contains('active-filter')
      };
    });

    if (!items.length) return null;

    var name = groupName(title.textContent);
    var activeCount = items.filter(function (i) {
      return i.isActive;
    }).length;

    var group = {
      key: name.replace(/[^a-z0-9]+/g, '-') || 'group-' + index,
      name: name,
      wrapper: wrapper,
      items: items,
      activeCount: activeCount,
      expanded: false // "show all" state, distinct from collapsed
    };

    wrapper.classList.add('pp-filter-group');

    /* --- collapsible header --- */

    var bodyId = 'pp-filter-body-' + index;
    title.classList.add('pp-filter-head');
    title.setAttribute('role', 'button');
    title.setAttribute('tabindex', '0');
    title.setAttribute('aria-controls', bodyId);

    if (activeCount) {
      title.appendChild(el('span', 'pp-filter-count', String(activeCount)));
    }
    title.appendChild(el('span', 'pp-filter-chevron'));

    /* The body is wrapped so height can be animated with grid-template-rows,
       which needs no measurement and survives the list being filtered. */
    var body = el('div', 'pp-filter-body');
    body.id = bodyId;
    var inner = el('div', 'pp-filter-body-inner');
    wrapper.insertBefore(body, list);
    body.appendChild(inner);

    /* --- search --- */

    var searchInput = null;

    if (items.length >= SEARCH_MIN) {
      var searchWrap = el('div', 'pp-filter-search');
      searchInput = el('input', 'pp-filter-search-input');
      searchInput.type = 'search';
      searchInput.setAttribute('autocomplete', 'off');
      searchInput.placeholder = 'Search ' + (name || 'options');
      searchInput.setAttribute('aria-label', 'Search ' + (name || 'options'));
      searchWrap.appendChild(searchInput);
      inner.appendChild(searchWrap);
    }

    inner.appendChild(list);

    var empty = el('p', 'pp-filter-empty', 'No matches');
    empty.hidden = true;
    inner.appendChild(empty);

    /* --- show all / show less --- */

    var moreBtn = null;

    if (items.length > VISIBLE_LIMIT) {
      moreBtn = el('button', 'pp-filter-more');
      moreBtn.type = 'button';
      inner.appendChild(moreBtn);
    }

    /* Which items are visible is the product of two independent things: the
       search query, and whether the list is capped. Recomputed in one place
       so the two can never disagree. */
    function render() {
      var query = searchInput ? normalise(searchInput.value) : '';
      var searching = query.length > 0;
      var shown = 0;
      var matches = 0;

      items.forEach(function (item) {
        var hit = !searching || item.search.indexOf(query) !== -1;
        if (hit) matches++;

        // An active filter always stays visible, so it can be un-picked even
        // when it falls outside the cap or the current search.
        var capped = !group.expanded && !searching && shown >= VISIBLE_LIMIT;
        var visible = hit && (!capped || item.isActive);

        item.node.classList.toggle('pp-filter-hidden', !visible);
        if (visible) shown++;
      });

      empty.hidden = matches !== 0;

      if (moreBtn) {
        // The cap is irrelevant mid-search; the query is already narrowing.
        moreBtn.hidden = searching || items.length <= VISIBLE_LIMIT;
        moreBtn.textContent = group.expanded ? 'Show less' : 'Show all ' + items.length;
        moreBtn.setAttribute('aria-expanded', group.expanded ? 'true' : 'false');
      }

      list.classList.toggle('pp-filter-scroll', group.expanded || searching);
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        group.expanded = !group.expanded;
        render();
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', render);
      // Stop the browser's search-field Enter from submitting anything.
      searchInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') event.preventDefault();
      });
    }

    /* --- collapse --- */

    group.collapsed = false;

    group.setCollapsed = function (value, animate) {
      group.collapsed = value;
      wrapper.classList.toggle('pp-filter-collapsed', value);
      if (!animate) wrapper.classList.add('pp-filter-no-anim');
      title.setAttribute('aria-expanded', value ? 'false' : 'true');
    };

    group.onToggle = null;

    function toggle() {
      group.setCollapsed(!group.collapsed, true);
      if (group.onToggle) group.onToggle(group);
    }

    title.addEventListener('click', toggle);
    title.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        toggle();
      }
    });

    group.render = render;
    render();

    return group;
  }

  /* ---------------------------------------------------------------- *
   *  Ranking and the "More filters" fold
   * ---------------------------------------------------------------- */

  function priorityIndex(name) {
    var i = PRIORITY.indexOf(name);
    if (i !== -1) return i;

    // Tolerate headings like "small pet type" matching the "type" entry.
    for (var p = 0; p < PRIORITY.length; p++) {
      if (name.indexOf(PRIORITY[p]) !== -1) return PRIORITY.length + p;
    }
    return 9999;
  }

  function organise(groups, collapsedState) {
    var parent = groups[0].wrapper.parentNode;
    if (!parent) return;

    var ranked = groups.slice().sort(function (a, b) {
      // A group holding an applied filter is always surfaced first.
      if (!!b.activeCount !== !!a.activeCount) return b.activeCount ? 1 : -1;

      var pa = priorityIndex(a.name);
      var pb = priorityIndex(b.name);
      if (pa !== pb) return pa - pb;

      // Then whichever offers more to choose between.
      if (b.items.length !== a.items.length) return b.items.length - a.items.length;
      return a.name.localeCompare(b.name);
    });

    var primary = [];
    var overflow = [];

    ranked.forEach(function (group, position) {
      // Never bury a group the shopper is currently filtering by.
      if (position < PRIMARY_GROUPS || group.activeCount) primary.push(group);
      else overflow.push(group);
    });

    // Re-lay the primary groups in rank order at the top of the container.
    primary.forEach(function (group) {
      parent.appendChild(group.wrapper);
    });

    var fold = null;

    if (overflow.length) {
      var toggle = el('button', 'pp-filter-overflow-btn');
      toggle.type = 'button';
      toggle.setAttribute('aria-controls', 'pp-filter-overflow');

      fold = el('div', 'pp-filter-overflow');
      fold.id = 'pp-filter-overflow';
      var foldInner = el('div', 'pp-filter-overflow-inner');
      fold.appendChild(foldInner);

      overflow.forEach(function (group) {
        foldInner.appendChild(group.wrapper);
      });

      parent.appendChild(toggle);
      parent.appendChild(fold);

      var open = readStore(STORE_KEY_MORE, false) === true;

      function paint(animate) {
        if (!animate) fold.classList.add('pp-filter-no-anim');
        fold.classList.toggle('pp-filter-overflow-open', open);
        toggle.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.textContent = open
          ? 'Fewer filters'
          : 'More filters (' + overflow.length + ')';
        toggle.appendChild(el('span', 'pp-filter-chevron'));
      }

      toggle.addEventListener('click', function () {
        open = !open;
        writeStore(STORE_KEY_MORE, open);
        paint(true);
      });

      paint(false);
    }

    /* Default collapse state. Only the first OPEN_BY_DEFAULT groups start
       open; everything else is folded so the column stays short. A stored
       preference wins, and a group with an applied filter is never closed. */
    ranked.forEach(function (group, position) {
      var stored = collapsedState[group.key];
      var collapsed =
        typeof stored === 'boolean' ? stored : position >= OPEN_BY_DEFAULT;

      if (group.activeCount) collapsed = false;

      group.setCollapsed(collapsed, false);

      group.onToggle = function (g) {
        collapsedState[g.key] = g.collapsed;
        writeStore(STORE_KEY, collapsedState);
      };
    });

    // Re-enable transitions after the first paint so restoring state does not
    // animate everything open and shut on load.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        groups.forEach(function (group) {
          group.wrapper.classList.remove('pp-filter-no-anim');
        });
        if (fold) fold.classList.remove('pp-filter-no-anim');
      });
    });
  }

  /* ---------------------------------------------------------------- *
   *  Boot
   * ---------------------------------------------------------------- */

  function initFilters() {
    var sidebar = document.querySelector('#sidebar');
    if (!sidebar) return;

    var stale = sidebar.querySelector('.pp-filter-summary');
    if (stale && stale.parentNode) stale.parentNode.removeChild(stale);

    var lists = [].slice.call(sidebar.querySelectorAll('.advanced-filters'));
    if (!lists.length) return;

    var collapsedState = readStore(STORE_KEY, {}) || {};
    var groups = [];

    lists.forEach(function (list, index) {
      var wrapper = list.closest ? list.closest('.widget-wrapper') : null;
      if (!wrapper) return;
      var group = enhanceGroup(wrapper, index);
      if (group) groups.push(group);
    });

    if (!groups.length) return;

    sidebar.classList.add('pp-filters-ready');
    organise(groups, collapsedState);
    buildSummary(sidebar, groups);
  }

  /* ---------------------------------------------------------------- *
   *  In-place filtering
   *
   *  The theme's filters are link_to_add_tag / link_to_remove_tag anchors,
   *  so every choice is an ordinary full-page navigation. The same URLs are
   *  fetched here and only the parts that actually change are swapped in -
   *  the product grid, the pagination and the filter lists.
   *
   *  Deliberately NOT swapped: the Show / Browse / Sort toolbar. Its handlers
   *  are bound once by kumi.script.js at load, and replacing the markup would
   *  silently kill the grid/list toggle. Its links stay ordinary navigations.
   *
   *  Anything unexpected - a non-OK response, a timeout, a page whose shape
   *  does not match - falls straight back to a real navigation, so the worst
   *  case is exactly today's behaviour.
   * ---------------------------------------------------------------- */

  var AJAX_TIMEOUT = 8000;
  var busy = false;

  function supportsAjax() {
    return !!(window.fetch && window.history && window.history.pushState && window.DOMParser);
  }

  function sameCollection(url) {
    try {
      var u = new URL(url, window.location.href);
      return u.origin === window.location.origin && u.pathname.indexOf('/collections/') === 0;
    } catch (e) {
      return false;
    }
  }

  function progressEl() {
    var bar = document.querySelector('.pp-filter-progress');
    if (!bar) {
      bar = el('div', 'pp-filter-progress');
      document.body.appendChild(bar);
    }
    return bar;
  }

  function setBusy(state) {
    var bar = progressEl();
    bar.classList.toggle('is-active', state);
    var grid = document.querySelector('#sandBox');
    if (grid) {
      grid.classList.toggle('pp-grid-busy', state);
      grid.setAttribute('aria-busy', state ? 'true' : 'false');
    }
    var side = document.querySelector('#sidebar .grid-uniform');
    if (side) side.classList.toggle('pp-grid-busy', state);
  }

  /* Apps bind to product cards at load. Anything with a documented re-render
     hook is nudged; the custom event gives anything else a way in. Each call
     is guarded because these globals are third-party and may change. */
  function reinitApps() {
    try {
      if (window.jdgm && typeof window.jdgm.batchRenderBadges === 'function') {
        window.jdgm.batchRenderBadges();
      }
    } catch (e) {}

    try {
      if (window.WishlistHero && typeof window.WishlistHero.RefreshItems === 'function') {
        window.WishlistHero.RefreshItems();
      }
    } catch (e) {}

    try {
      document.dispatchEvent(
        new CustomEvent('pp:collection:updated', { bubbles: true, detail: {} })
      );
    } catch (e) {}
  }

  /* The sort dropdown's active row is set by an inline script in
     collection-control.liquid. Injected markup never runs its scripts, so the
     value is read out of the incoming document and applied here instead. */
  function sortDefaultFrom(scope) {
    if (scope.dataset.sort) return scope.dataset.sort;
    var scripts = scope.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var match = /var\s+val_default\s*=\s*'([^']*)'/.exec(scripts[i].textContent || '');
      if (match) return match[1];
    }
    return null;
  }

  function applySort(value) {
    if (!value || !/^[a-z0-9-]+$/i.test(value)) return;

    var rows = document.querySelectorAll('#col-main .sortBy li.sort-by');
    [].forEach.call(rows, function (row) {
      row.classList.remove('active');
    });

    var target = document.querySelector('#col-main .sortBy li.sort-by.' + value);
    if (target) target.classList.add('active');
  }

  /* #col-main is replaced wholesale rather than piecemeal. That covers the
     awkward cases in one go - a zero-result page (which renders no #sandBox
     at all), a ?view= change that swaps template, and the toolbar's own
     active states - none of which a partial swap handles cleanly.

     Replacing the toolbar is only safe because the handlers that live on it
     are delegated from document below, so they survive the markup being
     rebuilt. Bootstrap's dropdowns are delegated too, via its data-api. */
  function swapIn(doc, url) {
    var oldMain = document.querySelector('#col-main');
    var newMain = doc.querySelector('#col-main');
    if (!oldMain || !newMain) return false;

    // kumi.script.js records list view as .full_width on #sandBox. The fresh
    // markup always comes back as grid, so carry the choice across.
    var wasList = !!document.querySelector('#sandBox.full_width');

    oldMain.className = newMain.className;
    oldMain.dataset.sort = newMain.dataset.sort;
    oldMain.innerHTML = newMain.innerHTML;

    if (wasList) {
      var grid = oldMain.querySelector('#sandBox');
      if (grid) grid.classList.add('full_width');

      var gridBtn = oldMain.querySelector('.grid_list .grid');
      var listBtn = oldMain.querySelector('.grid_list .list');
      if (gridBtn && listBtn) {
        gridBtn.classList.remove('active');
        listBtn.classList.add('active');
        gridBtn.setAttribute('aria-pressed', 'false');
        listBtn.setAttribute('aria-pressed', 'true');
      }
    }

    applySort(sortDefaultFrom(newMain));

    // Switching collections through the Browse menu changes the trail.
    var oldCrumb = document.querySelector('.breadcrumb');
    var newCrumb = doc.querySelector('.breadcrumb');
    if (oldCrumb && newCrumb) oldCrumb.innerHTML = newCrumb.innerHTML;

    var oldSide = document.querySelector('#sidebar .grid-uniform');
    var newSide = doc.querySelector('#sidebar .grid-uniform');
    if (oldSide && newSide) oldSide.parentNode.replaceChild(newSide, oldSide);
    else if (oldSide && !newSide) oldSide.parentNode.removeChild(oldSide);

    initFilters();
    reinitApps();
    return true;
  }

  function navigate(url, isPop) {
    if (busy) return;
    busy = true;
    setBusy(true);

    var controller = window.AbortController ? new AbortController() : null;
    var timer = window.setTimeout(function () {
      if (controller) controller.abort();
    }, AJAX_TIMEOUT);

    function fallback() {
      window.clearTimeout(timer);
      window.location.href = url;
    }

    window
      .fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        window.clearTimeout(timer);
        var doc = new DOMParser().parseFromString(html, 'text/html');

        if (!swapIn(doc, url)) {
          fallback();
          return;
        }

        if (!isPop) window.history.pushState({ pp: true }, '', url);
        if (doc.title) document.title = doc.title;

        // Only pull the page back up if the grid has scrolled out of view.
        var main = document.querySelector('#col-main');
        if (main && main.getBoundingClientRect().top < -8) {
          window.scrollTo({ top: main.offsetTop - 16, behavior: 'smooth' });
        }

        busy = false;
        setBusy(false);
      })
      .catch(fallback);
  }

  function onFilterClick(event) {
    if (event.defaultPrevented) return;
    // Let the browser own modified clicks - new tab, download, middle click.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button && event.button !== 0) return;

    /* Deliberately excludes `.load_more a`: with infinite scroll enabled the
       theme's own handler appends the next page, and swapping the grid here
       would replace it instead. */
    var link = event.target.closest
      ? event.target.closest(
          '.advanced-filter a, .pp-filter-chip, .pp-filter-clear-all,' +
            '.pagination a, .collection-actions .dropdown-menu a'
        )
      : null;
    if (!link) return;

    var href0 = link.getAttribute('href');
    if (!href0 || href0.charAt(0) === '#' || href0.indexOf('javascript:') === 0) return;

    var href = href0;
    if (!sameCollection(href)) return;

    var row = link.closest('.advanced-filter');

    if (row) {
      /* Reproduces the group-exclusive rule from the inline script in
         collection-filter.liquid: picking a second value in a group replaces
         the first rather than stacking. That script also listens on .advanced-filter
         and would call window.location, so propagation is stopped below. */
      var group = row.getAttribute('data-group');
      var current = group
        ? document.querySelector('.advanced-filter.active-filter[data-group="' + group + '"]')
        : null;

      if (current && current !== row) {
        var handle = current.getAttribute('data-handle');
        if (handle) {
          var targetUrl = new URL(href, window.location.href);
          var segments = targetUrl.pathname.split('/');
          var tagIndex = segments.indexOf('collections') + 2;
          segments[tagIndex] = (segments[tagIndex] || '').split('+').filter(function (tag) { return tag !== handle; }).join('+');
          targetUrl.pathname = segments.join('/');
          href = targetUrl.pathname + targetUrl.search;
        }
      }

      // Optimistic tick so the choice registers on the click, not on arrival.
      row.classList.add('pp-filter-pending');
    }

    event.preventDefault();
    event.stopPropagation();
    var nextUrl = new URL(href, window.location.href);
    if (row || link.matches('.pp-filter-chip, .pp-filter-clear-all')) {
      var currentParams = new URLSearchParams(window.location.search);
      ['sort_by', 'view'].forEach(function (key) {
        if (currentParams.has(key)) nextUrl.searchParams.set(key, currentParams.get(key));
      });
      nextUrl.searchParams.delete('page');
    }
    navigate(nextUrl.pathname + nextUrl.search, false);
  }

  /* Sorting has no href to intercept - collection-control.liquid renders
     href="javascript:;" and an inline handler that assigns location.search.
     The equivalent URL is built here and fetched instead. Propagation is
     stopped so that inline handler does not also navigate. */
  function onSortClick(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button && event.button !== 0) return;

    var row = event.target.closest ? event.target.closest('.sortBy li.sort-by') : null;
    if (!row) return;

    event.preventDefault();
    event.stopPropagation();

    if (row.classList.contains('active')) return;

    var value = row.getAttribute('data-sort');
    if (!value) return;

    try {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      navigate(url.pathname + url.search, false);
    } catch (e) {
      /* leave the page as it is rather than half-applying a sort */
    }
  }

  /* kumi.script.js binds the grid/list toggle once at load, so it is lost the
     moment #col-main is rebuilt. This delegated version replaces it. It
     assigns absolute state rather than toggling, so on the initial markup -
     where the theme's handler is still attached - both run to the same
     result instead of cancelling each other out. */
  function onGridListClick(event) {
    var span = event.target.closest ? event.target.closest('.grid_list button') : null;
    if (!span) return;

    var wrap = span.closest('.grid_list');
    if (!wrap) return;

    var isList = span.classList.contains('list');

    [].forEach.call(wrap.querySelectorAll('button'), function (node) {
      node.classList.toggle('active', node === span);
      node.setAttribute('aria-pressed', node === span ? 'true' : 'false');
    });

    var grid = document.querySelector('#sandBox');
    if (grid) grid.classList.toggle('full_width', isList);
  }

  ready(function () {
    if (!document.body.classList.contains('template-collection')) return;

    initFilters();

    if (!supportsAjax()) return;
    if (!document.querySelector('#col-main')) return;

    // Capture phase, so these run before the theme's jQuery handlers on the
    // parent .advanced-filter row and on .sortBy li.
    document.addEventListener('click', onFilterClick, true);
    document.addEventListener('click', onSortClick, true);
    document.addEventListener('click', onGridListClick);
    document.addEventListener('click', function (event) {
      document.querySelectorAll('.qn-collection-dropdown[open]').forEach(function (dropdown) {
        if (!dropdown.contains(event.target)) dropdown.open = false;
      });
    });
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('.qn-collection-dropdown[open]').forEach(function (dropdown) {
        dropdown.open = false;
        dropdown.querySelector('summary').focus();
      });
    });

    window.history.replaceState({ pp: true }, '');

    window.addEventListener('popstate', function (event) {
      if (event.state && event.state.pp) navigate(window.location.href, true);
      else window.location.reload();
    });
  });
})();
